import React, { useState, useEffect } from 'react';
import { usePageMeta } from '../hooks/usePageMeta';
import { Package, TrendingUp, Users, Shield, BarChart3, Clock, Truck, MapPin, CheckCircle2, ArrowRight, Star, Zap, Globe, FileText, Menu, X, Code, Sparkles, Eye, Glasses, Wallet, ShoppingCart, FileCheck, Layers, CreditCard, MessageSquare, Cloud, Settings, Bot } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import DashboardMockup from './DashboardMockup';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping for dynamic features
const iconMap = {
  Eye: Eye,
  Glasses: Glasses,
  MapPin: MapPin,
  Zap: Zap,
  Sparkles: Sparkles,
  Package: Package,
  Truck: Truck,
  Globe: Globe,
  Users: Users,
  FileText: FileText,
  Wallet: Wallet,
  Code: Code,
  ShoppingCart: ShoppingCart,
  FileContract: FileCheck,
  CreditCard: CreditCard,
  Layers: Layers,
  Shield: Shield,
  BarChart3: BarChart3,
  MessageSquare: MessageSquare,
  Cloud: Cloud,
  Settings: Settings,
  Bot: Bot
};

const LandingPage = () => {
  usePageMeta('SpedireOnline | Automazione Logistica AI e Spedizioni', 'SpedireOnline.net: piattaforma AI per gestire spedizioni nazionali e internazionali. VERONICA automatizza logistica e customer care, tracking corrieri e confronto tariffe DHL, UPS, FedEx, GLS, BRT.');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [demoForm, setDemoForm] = useState({ name: '', email: '', company: '', phone: '' });
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [demoLoading, setDemoLoading] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [activeNews, setActiveNews] = useState([]);

  // Load active news on component mount
  useEffect(() => {
    const loadNews = async () => {
      try {
        const response = await axios.get(`${API}/news`);
        setActiveNews(response.data || []);
      } catch (error) {
        console.error('Error loading news:', error);
        // Fallback to empty - the static section will still show
      }
    };
    loadNews();
  }, []);

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    setDemoLoading(true);
    
    try {
      const response = await axios.post(`${API}/demo-request`, demoForm);
      if (response.data.success) {
        toast.success(response.data.message);
        setDemoForm({ name: '', email: '', company: '', phone: '' });
      }
    } catch (error) {
      console.error('Demo request error:', error);
      toast.error(error.response?.data?.detail || 'Errore durante l\'invio della richiesta');
    } finally {
      setDemoLoading(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactLoading(true);
    
    try {
      const response = await axios.post(`${API}/contact`, contactForm);
      if (response.data.success) {
        toast.success(response.data.message);
        setContactForm({ name: '', email: '', message: '' });
      }
    } catch (error) {
      console.error('Contact error:', error);
      toast.error(error.response?.data?.detail || 'Errore durante l\'invio del messaggio');
    } finally {
      setContactLoading(false);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setNewsletterLoading(true);
    
    try {
      const response = await axios.post(`${API}/newsletter`, { email: newsletterEmail });
      if (response.data.success) {
        toast.success(response.data.message);
        setNewsletterEmail('');
      }
    } catch (error) {
      console.error('Newsletter error:', error);
      if (error.response?.status === 409) {
        toast.error('Questa email è già iscritta alla newsletter');
      } else {
        toast.error(error.response?.data?.detail || 'Errore durante l\'iscrizione');
      }
    } finally {
      setNewsletterLoading(false);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="App">
      {/* Navigation */}
      <nav className="nav-header">
        <div className="flex items-center gap-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_ship-smart-6/artifacts/4h2rj0xf_logoonly.png" 
            alt="SpedireOnline.net - Piattaforma gestione spedizioni" 
            className="h-10 w-10 object-contain"
          />
          <span className="text-xl font-bold" style={{ color: 'var(--brand-primary)' }}>spedireonline.net</span>
        </div>
        
        <div className="hidden md:flex items-center gap-6">
          {activeNews.length > 0 && (
            <button onClick={() => scrollToSection('innovation')} className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Novità</button>
          )}
          <button onClick={() => scrollToSection('features')} className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Funzionalità</button>
          <button onClick={() => scrollToSection('plugins')} className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Plugin</button>
          <a href="/developers" className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>API</a>
          <button onClick={() => scrollToSection('access')} className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Accedi</button>
          <button onClick={() => scrollToSection('demo')} className="btn-primary">Richiedi Demo</button>
        </div>

        <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="fixed top-16 left-0 right-0 bg-white border-b shadow-lg z-40 md:hidden">
          <div className="flex flex-col p-4 gap-3">
            {activeNews.length > 0 && (
              <button onClick={() => scrollToSection('innovation')} className="text-left py-2">Novità</button>
            )}
            <button onClick={() => scrollToSection('features')} className="text-left py-2">Funzionalità</button>
            <button onClick={() => scrollToSection('plugins')} className="text-left py-2">Plugin</button>
            <a href="/developers" className="text-left py-2">API</a>
            <button onClick={() => scrollToSection('access')} className="text-left py-2">Accedi</button>
            <button onClick={() => scrollToSection('demo')} className="btn-primary w-full">Richiedi Demo</button>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="hero-section" itemScope itemType="https://schema.org/WebPage">
        <div className="hero-content text-center">
          <div className="inline-block px-4 py-2 rounded-full mb-6" style={{ background: 'var(--accent-wash)', color: 'var(--accent-text)' }}>
            <span className="text-sm font-semibold">🚀 Gestisci tutte le tue spedizioni in un unico posto</span>
          </div>
          <h1 className="heading-1 mb-6">La piattaforma intelligente per le tue spedizioni</h1>
          <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>
            Confronta tariffe, crea etichette e traccia tutte le tue spedizioni nazionali e internazionali con un'unica soluzione completa.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => window.location.href = '/spedizione'} className="btn-primary" data-testid="hero-spedisci-btn">
              <Package className="w-5 h-5" /> Spedisci un pacco ora
            </button>
            <button onClick={() => scrollToSection('demo')} className="btn-secondary">
              Richiedi Demo Gratuita <ArrowRight className="w-5 h-5" />
            </button>
          </div>
          <div className="mt-12 flex flex-wrap justify-center gap-8 text-sm" style={{ color: 'var(--text-muted)' }}>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
              <span>Nessun costo iniziale</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
              <span>Setup in 5 minuti</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
              <span>Supporto italiano</span>
            </div>
          </div>
        </div>
      </section>

      {/* Innovation/News Sections - Dynamic from Database */}
      {activeNews.map((newsItem, newsIndex) => (
        <section 
          key={newsItem.id} 
          id={newsIndex === 0 ? "innovation" : `news-${newsItem.section_key}`} 
          className="section" 
          style={{ 
            background: newsIndex % 2 === 0 
              ? 'linear-gradient(135deg, #0a0a1a 0%, #1a1a3e 50%, #0d1f2d 100%)' 
              : 'linear-gradient(135deg, #0d1f2d 0%, #1a1a3e 50%, #0a0a1a 100%)'
          }}
        >
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              {newsItem.badge_text && (
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ background: `${newsItem.badge_color}22`, border: `1px solid ${newsItem.badge_color}55` }}>
                  <Sparkles className="w-4 h-4" style={{ color: newsItem.badge_color }} />
                  <span className="text-sm font-semibold" style={{ color: newsItem.badge_color }}>{newsItem.badge_text}</span>
                </div>
              )}
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-2" style={{ color: 'white' }}>
                {newsItem.title}
              </h2>
              {newsItem.subtitle && (
                <p className="text-xl font-medium mb-4" style={{ color: newsItem.highlight_color }}>
                  {newsItem.subtitle}
                </p>
              )}
              <p className="text-base lg:text-lg max-w-3xl mx-auto" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {newsItem.description}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* Main Image */}
              {newsItem.images?.[0] && (
                <div className="relative rounded-2xl overflow-hidden" style={{ aspectRatio: '16/10' }}>
                  <img 
                    src={newsItem.images[0].url} 
                    alt={newsItem.images[0].alt}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 50%)' }}></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="text-sm font-medium" style={{ color: newsItem.highlight_color }}>{newsItem.badge_text || 'Novità'}</p>
                    <p className="text-white text-lg font-semibold">{newsItem.images[0].caption || newsItem.subtitle || newsItem.title}</p>
                  </div>
                </div>
              )}

              {/* Features Grid */}
              <div className={`grid ${newsItem.features?.length > 4 ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-2'} gap-4`}>
                {newsItem.features?.slice(0, 6).map((feature, idx) => {
                  const IconComponent = iconMap[feature.icon] || Sparkles;
                  return (
                    <div key={idx} className="rounded-xl p-4" style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ background: `${feature.color}22` }}>
                        <IconComponent className="w-5 h-5" style={{ color: feature.color }} />
                      </div>
                      <h3 className="font-semibold mb-1" style={{ color: 'white' }}>{feature.title}</h3>
                      <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>{feature.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Images Gallery */}
            {newsItem.images?.length > 1 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                {newsItem.images.slice(1, 4).map((img, idx) => (
                  <div key={img.url || idx} className="rounded-xl overflow-hidden" style={{ aspectRatio: '4/3' }}>
                    <img 
                      src={img.url} 
                      alt={img.alt}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Call to Action */}
            <div className="text-center">
              <div className="inline-flex flex-col sm:flex-row gap-4">
                {newsItem.cta_primary_text && (
                  <button 
                    onClick={() => {
                      if (newsItem.cta_primary_link?.startsWith('/')) {
                        window.location.href = newsItem.cta_primary_link;
                      } else {
                        scrollToSection(newsItem.cta_primary_link?.replace('#', '') || 'demo');
                      }
                    }} 
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all" 
                    style={{ background: newsItem.highlight_color, color: '#0a0a1a' }}
                  >
                    {newsItem.cta_primary_text}
                    <ArrowRight className="w-5 h-5" />
                  </button>
                )}
                {newsItem.cta_secondary_text && (
                  <button 
                    onClick={() => {
                      if (newsItem.cta_secondary_link?.startsWith('/')) {
                        window.location.href = newsItem.cta_secondary_link;
                      } else {
                        scrollToSection(newsItem.cta_secondary_link?.replace('#', '') || 'contact');
                      }
                    }} 
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all" 
                    style={{ background: 'transparent', border: '1px solid rgba(255, 255, 255, 0.3)', color: 'white' }}
                  >
                    {newsItem.cta_secondary_text}
                    <ArrowRight className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Problem Section */}
      <section className="section section-alt">
        <div className="text-center mb-12">
          <h2 className="heading-2 mb-4">I problemi che risolviamo</h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>Le sfide quotidiane nella gestione delle spedizioni</p>
        </div>
        <div className="grid-3">
          <Card className="feature-card">
            <CardHeader>
              <div className="icon-container">
                <TrendingUp className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
              </div>
              <CardTitle style={{ color: 'var(--text-primary)' }}>Gestione Complessa</CardTitle>
            </CardHeader>
            <CardContent>
              <p style={{ color: 'var(--text-secondary)' }}>Difficoltà nel gestire spedizioni con più corrieri diversi, perdendo tempo prezioso.</p>
            </CardContent>
          </Card>
          <Card className="feature-card">
            <CardHeader>
              <div className="icon-container">
                <MapPin className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
              </div>
              <CardTitle style={{ color: 'var(--text-primary)' }}>Scarsa Tracciabilità</CardTitle>
            </CardHeader>
            <CardContent>
              <p style={{ color: 'var(--text-secondary)' }}>Impossibilità di monitorare in tempo reale tutte le spedizioni da un'unica piattaforma.</p>
            </CardContent>
          </Card>
          <Card className="feature-card">
            <CardHeader>
              <div className="icon-container">
                <BarChart3 className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
              </div>
              <CardTitle style={{ color: 'var(--text-primary)' }}>Costi Non Ottimizzati</CardTitle>
            </CardHeader>
            <CardContent>
              <p style={{ color: 'var(--text-secondary)' }}>Mancanza di confronto tariffe in tempo reale per scegliere l'opzione più conveniente.</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Solution Section */}
      <section className="section">
        <div className="text-center mb-12">
          <h2 className="heading-2 mb-4">La nostra soluzione</h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>Un'unica piattaforma che integra tutti i corrieri principali</p>
        </div>
        <div className="grid-2">
          <div className="flex flex-col justify-center gap-6">
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
                  <Zap className="w-6 h-6" style={{ color: 'var(--accent-primary)' }} />
                </div>
              </div>
              <div>
                <h3 className="heading-3 mb-2">Integrazione Multi-Corriere</h3>
                <p style={{ color: 'var(--text-secondary)' }}>Tutti i corrieri principali in un'unica interfaccia intuitiva.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
                  <Truck className="w-6 h-6" style={{ color: 'var(--accent-primary)' }} />
                </div>
              </div>
              <div>
                <h3 className="heading-3 mb-2">Automazione Completa</h3>
                <p style={{ color: 'var(--text-secondary)' }}>Automatizza i processi di spedizione dalla creazione alla consegna.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
                  <BarChart3 className="w-6 h-6" style={{ color: 'var(--accent-primary)' }} />
                </div>
              </div>
              <div>
                <h3 className="heading-3 mb-2">Monitoraggio Centralizzato</h3>
                <p style={{ color: 'var(--text-secondary)' }}>Dashboard analitica con metriche e KPI in tempo reale.</p>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-center">
            <div className="w-full h-96 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, var(--brand-primary-light), var(--accent-wash))' }}>
              <Package className="w-32 h-32 animate-float" style={{ color: 'var(--accent-primary)', opacity: 0.3 }} />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="section section-alt">
        <div className="text-center mb-12">
          <h2 className="heading-2 mb-4">Funzionalità principali</h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>Tutto ciò di cui hai bisogno per gestire le tue spedizioni</p>
        </div>
        <div className="grid-3">
          <Card className="feature-card">
            <CardHeader>
              <div className="icon-container">
                <FileText className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
              </div>
              <CardTitle style={{ color: 'var(--text-primary)' }}>Creazione Etichette</CardTitle>
            </CardHeader>
            <CardContent>
              <p style={{ color: 'var(--text-secondary)' }}>Crea e stampa etichette di spedizione in pochi click, pronte all'uso.</p>
            </CardContent>
          </Card>
          <Card className="feature-card">
            <CardHeader>
              <div className="icon-container">
                <BarChart3 className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
              </div>
              <CardTitle style={{ color: 'var(--text-primary)' }}>Comparazione Tariffe</CardTitle>
            </CardHeader>
            <CardContent>
              <p style={{ color: 'var(--text-secondary)' }}>Confronta tariffe in tempo reale e scegli il corriere più conveniente.</p>
            </CardContent>
          </Card>
          <Card className="feature-card">
            <CardHeader>
              <div className="icon-container">
                <MapPin className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
              </div>
              <CardTitle style={{ color: 'var(--text-primary)' }}>Tracking Real-Time</CardTitle>
            </CardHeader>
            <CardContent>
              <p style={{ color: 'var(--text-secondary)' }}>Monitora tutte le spedizioni in tempo reale con notifiche automatiche.</p>
            </CardContent>
          </Card>
          <Card className="feature-card">
            <CardHeader>
              <div className="icon-container">
                <Users className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
              </div>
              <CardTitle style={{ color: 'var(--text-primary)' }}>Rubrica Clienti</CardTitle>
            </CardHeader>
            <CardContent>
              <p style={{ color: 'var(--text-secondary)' }}>Gestisci i tuoi clienti e lo storico delle spedizioni in modo organizzato.</p>
            </CardContent>
          </Card>
          <Card className="feature-card">
            <CardHeader>
              <div className="icon-container">
                <Globe className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
              </div>
              <CardTitle style={{ color: 'var(--text-primary)' }}>API E-commerce</CardTitle>
            </CardHeader>
            <CardContent>
              <p style={{ color: 'var(--text-secondary)' }}>Integrazione con Shopify, WooCommerce, Magento e altri e-commerce.</p>
            </CardContent>
          </Card>
          <Card className="feature-card">
            <CardHeader>
              <div className="icon-container">
                <TrendingUp className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
              </div>
              <CardTitle style={{ color: 'var(--text-primary)' }}>Dashboard Analitica</CardTitle>
            </CardHeader>
            <CardContent>
              <p style={{ color: 'var(--text-secondary)' }}>Analizza performance, KPI e tendenze con report dettagliati.</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Dashboard Mockup Section */}
      <section id="dashboard" className="section">
        <div className="text-center mb-12">
          <h2 className="heading-2 mb-4">Dashboard di gestione</h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>Visualizza e controlla tutte le tue spedizioni da un'unica interfaccia professionale</p>
        </div>
        <DashboardMockup />
      </section>

      {/* Commercial Plugins Section */}
      <section id="plugins" className="section section-alt">
        <div className="text-center mb-12">
          <h2 className="heading-2 mb-4">I nostri plugin commerciali</h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>Integra spedireonline.net con le principali piattaforme e-commerce</p>
        </div>
        <div className="grid-3">
          <Card className="feature-card">
            <CardHeader>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-lg flex items-center justify-center text-2xl font-bold" style={{ background: 'var(--accent-wash)', color: 'var(--accent-primary)' }}>
                  S
                </div>
                <div>
                  <CardTitle style={{ color: 'var(--text-primary)' }}>Shopify</CardTitle>
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Plugin ufficiale</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p style={{ color: 'var(--text-secondary)' }} className="mb-4">Integrazione completa per il tuo store Shopify. Sincronizzazione automatica ordini e tracking.</p>
              <ul className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                <li className="flex gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                  <span>Importazione automatica ordini</span>
                </li>
                <li className="flex gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                  <span>Generazione etichette in bulk</span>
                </li>
                <li className="flex gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                  <span>Aggiornamento tracking automatico</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="feature-card">
            <CardHeader>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-lg flex items-center justify-center text-2xl font-bold" style={{ background: 'var(--accent-wash)', color: 'var(--accent-primary)' }}>
                  W
                </div>
                <div>
                  <CardTitle style={{ color: 'var(--text-primary)' }}>WooCommerce</CardTitle>
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>WordPress plugin</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p style={{ color: 'var(--text-secondary)' }} className="mb-4">Plugin nativo per WooCommerce con gestione completa dal pannello WordPress.</p>
              <ul className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                <li className="flex gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                  <span>Integrazione pannello admin</span>
                </li>
                <li className="flex gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                  <span>Calcolo spese spedizione real-time</span>
                </li>
                <li className="flex gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                  <span>Widget tracking per clienti</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="feature-card">
            <CardHeader>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-lg flex items-center justify-center text-2xl font-bold" style={{ background: 'var(--accent-wash)', color: 'var(--accent-primary)' }}>
                  M
                </div>
                <div>
                  <CardTitle style={{ color: 'var(--text-primary)' }}>Magento</CardTitle>
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Extension enterprise</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p style={{ color: 'var(--text-secondary)' }} className="mb-4">Extension professionale per Magento 2.x con funzionalità avanzate per grandi volumi.</p>
              <ul className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                <li className="flex gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                  <span>Gestione multi-store</span>
                </li>
                <li className="flex gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                  <span>Regole di spedizione avanzate</span>
                </li>
                <li className="flex gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                  <span>Reporting e analytics</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="feature-card">
            <CardHeader>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-lg flex items-center justify-center text-2xl font-bold" style={{ background: 'var(--accent-wash)', color: 'var(--accent-primary)' }}>
                  P
                </div>
                <div>
                  <CardTitle style={{ color: 'var(--text-primary)' }}>PrestaShop</CardTitle>
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Modulo ufficiale</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p style={{ color: 'var(--text-secondary)' }} className="mb-4">Modulo completo per PrestaShop con interfaccia in italiano e supporto dedicato.</p>
              <ul className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                <li className="flex gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                  <span>Configurazione semplificata</span>
                </li>
                <li className="flex gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                  <span>Stampa etichette da backoffice</span>
                </li>
                <li className="flex gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                  <span>Email tracking automatiche</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="feature-card">
            <CardHeader>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-lg flex items-center justify-center text-2xl font-bold" style={{ background: 'var(--accent-wash)', color: 'var(--accent-primary)' }}>
                  E
                </div>
                <div>
                  <CardTitle style={{ color: 'var(--text-primary)' }}>eBay</CardTitle>
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Connettore marketplace</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p style={{ color: 'var(--text-secondary)' }} className="mb-4">Connessione diretta con il tuo account eBay per gestire vendite e spedizioni.</p>
              <ul className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                <li className="flex gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                  <span>Import ordini venduti</span>
                </li>
                <li className="flex gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                  <span>Caricamento tracking su eBay</span>
                </li>
                <li className="flex gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                  <span>Gestione feedback automatica</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="feature-card">
            <CardHeader>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-lg flex items-center justify-center text-2xl font-bold" style={{ background: 'var(--accent-wash)', color: 'var(--accent-primary)' }}>
                  A
                </div>
                <div>
                  <CardTitle style={{ color: 'var(--text-primary)' }}>Amazon</CardTitle>
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Amazon Seller Central</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p style={{ color: 'var(--text-secondary)' }} className="mb-4">Integrazione per venditori Amazon con gestione FBA e spedizioni dirette.</p>
              <ul className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                <li className="flex gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                  <span>Sincronizzazione ordini MFN</span>
                </li>
                <li className="flex gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                  <span>Buy Shipping integration</span>
                </li>
                <li className="flex gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                  <span>Conformità requisiti Amazon</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

        <div className="mt-12 text-center">
          <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>Non trovi la tua piattaforma? Contattaci per integrazioni personalizzate</p>
          <button onClick={() => scrollToSection('contact')} className="btn-primary">
            Richiedi Integrazione Personalizzata <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="section section-alt">
        <div className="text-center mb-12">
          <h2 className="heading-2 mb-4">Vantaggi per i nostri utenti</h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>Perché scegliere spedireonline.net</p>
        </div>
        <div className="grid-2">
          <Card className="feature-card">
            <CardHeader>
              <div className="icon-container">
                <Zap className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
              </div>
              <CardTitle style={{ color: 'var(--text-primary)' }}>Interfaccia Intuitiva</CardTitle>
            </CardHeader>
            <CardContent>
              <p style={{ color: 'var(--text-secondary)' }}>Design moderno e user-friendly, utilizzabile da chiunque senza formazione.</p>
            </CardContent>
          </Card>
          <Card className="feature-card">
            <CardHeader>
              <div className="icon-container">
                <Truck className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
              </div>
              <CardTitle style={{ color: 'var(--text-primary)' }}>Multi-Corriere</CardTitle>
            </CardHeader>
            <CardContent>
              <p style={{ color: 'var(--text-secondary)' }}>Tutti i corrieri principali in un'unica vista. DHL, UPS, FedEx, GLS, BRT e molti altri.</p>
            </CardContent>
          </Card>
          <Card className="feature-card">
            <CardHeader>
              <div className="icon-container">
                <Clock className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
              </div>
              <CardTitle style={{ color: 'var(--text-primary)' }}>Risparmio di Tempo</CardTitle>
            </CardHeader>
            <CardContent>
              <p style={{ color: 'var(--text-secondary)' }}>Riduci fino al 70% il tempo dedicato alla gestione delle spedizioni.</p>
            </CardContent>
          </Card>
          <Card className="feature-card">
            <CardHeader>
              <div className="icon-container">
                <Star className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
              </div>
              <CardTitle style={{ color: 'var(--text-primary)' }}>Supporto Dedicato</CardTitle>
            </CardHeader>
            <CardContent>
              <p style={{ color: 'var(--text-secondary)' }}>Assistenza clienti rapida e professionale in italiano, sempre al tuo fianco.</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Security Section */}
      <section className="section">
        <div className="text-center mb-12">
          <h2 className="heading-2 mb-4">Sicurezza e tecnologia</h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>La tua sicurezza è la nostra priorità</p>
        </div>
        <div className="grid-3">
          <div className="text-center">
            <div className="icon-container mx-auto">
              <Shield className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
            </div>
            <h3 className="heading-3 mb-2">GDPR Compliant</h3>
            <p style={{ color: 'var(--text-secondary)' }}>Conformità totale al regolamento europeo sulla privacy</p>
          </div>
          <div className="text-center">
            <div className="icon-container mx-auto">
              <Globe className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
            </div>
            <h3 className="heading-3 mb-2">API RESTful</h3>
            <p style={{ color: 'var(--text-secondary)' }}>Tecnologia moderna, sicura e scalabile</p>
          </div>
          <div className="text-center">
            <div className="icon-container mx-auto">
              <CheckCircle2 className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
            </div>
            <h3 className="heading-3 mb-2">Uptime 99.9%</h3>
            <p style={{ color: 'var(--text-secondary)' }}>Hosting cloud affidabile sempre disponibile</p>
          </div>
        </div>
      </section>

      {/* Target Audience Section */}
      <section className="section section-alt">
        <div className="text-center mb-12">
          <h2 className="heading-2 mb-4">Per chi è pensata la piattaforma</h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>Soluzioni su misura per ogni esigenza</p>
        </div>
        <div className="grid-3">
          <Card className="feature-card text-center">
            <CardContent className="pt-6">
              <Users className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--accent-primary)' }} />
              <h3 className="heading-3 mb-3">Privati e PMI</h3>
              <p style={{ color: 'var(--text-secondary)' }}>Gestisci facilmente le tue spedizioni occasionali o regolari</p>
            </CardContent>
          </Card>
          <Card className="feature-card text-center">
            <CardContent className="pt-6">
              <Package className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--accent-primary)' }} />
              <h3 className="heading-3 mb-3">E-commerce</h3>
              <p style={{ color: 'var(--text-secondary)' }}>Integrazione perfetta con il tuo negozio online</p>
            </CardContent>
          </Card>
          <Card className="feature-card text-center">
            <CardContent className="pt-6">
              <Truck className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--accent-primary)' }} />
              <h3 className="heading-3 mb-3">Operatori Logistici</h3>
              <p style={{ color: 'var(--text-secondary)' }}>Gestione avanzata per volumi elevati di spedizioni</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* How it Works Section */}
      <section className="section">
        <div className="text-center mb-12">
          <h2 className="heading-2 mb-4">Come funziona</h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>Semplice, veloce ed efficace in 4 passaggi</p>
        </div>
        <div className="grid-2">
          <div className="flex gap-4 items-start">
            <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-white font-bold" style={{ background: 'var(--accent-primary)' }}>
              1
            </div>
            <div>
              <h3 className="heading-3 mb-2">Inserisci mittente e destinatario</h3>
              <p style={{ color: 'var(--text-secondary)' }}>Compila i dati di spedizione in modo rapido, anche utilizzando la rubrica salvata</p>
            </div>
          </div>
          <div className="flex gap-4 items-start">
            <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-white font-bold" style={{ background: 'var(--accent-primary)' }}>
              2
            </div>
            <div>
              <h3 className="heading-3 mb-2">Scegli il corriere più conveniente</h3>
              <p style={{ color: 'var(--text-secondary)' }}>Confronta prezzi e tempi di consegna per tutti i corrieri disponibili</p>
            </div>
          </div>
          <div className="flex gap-4 items-start">
            <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-white font-bold" style={{ background: 'var(--accent-primary)' }}>
              3
            </div>
            <div>
              <h3 className="heading-3 mb-2">Stampa l'etichetta</h3>
              <p style={{ color: 'var(--text-secondary)' }}>Genera e stampa l'etichetta di spedizione pronta da applicare al pacco</p>
            </div>
          </div>
          <div className="flex gap-4 items-start">
            <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-white font-bold" style={{ background: 'var(--accent-primary)' }}>
              4
            </div>
            <div>
              <h3 className="heading-3 mb-2">Traccia in tempo reale</h3>
              <p style={{ color: 'var(--text-secondary)' }}>Monitora lo stato della spedizione fino alla consegna con notifiche automatiche</p>
            </div>
          </div>
        </div>
      </section>

      {/* Integrations Section */}
      <section className="section section-alt">
        <div className="text-center mb-12">
          <h2 className="heading-2 mb-4">Integrazioni e API</h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>Connetti spedireonline.net con i tuoi strumenti</p>
        </div>
        <div className="max-w-3xl mx-auto">
          <a href="/developers" className="block">
            <Card className="feature-card cursor-pointer">
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row gap-8 items-center">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-4">
                      <h3 className="heading-3">API Documentata</h3>
                      <ArrowRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--accent-primary)' }} />
                    </div>
                    <ul className="space-y-3">
                      <li className="flex gap-3">
                        <CheckCircle2 className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                        <span style={{ color: 'var(--text-secondary)' }}>Collegamento con gestionali aziendali</span>
                      </li>
                      <li className="flex gap-3">
                        <CheckCircle2 className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                        <span style={{ color: 'var(--text-secondary)' }}>Integrazione e-commerce (Shopify, WooCommerce, Magento)</span>
                      </li>
                      <li className="flex gap-3">
                        <CheckCircle2 className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                        <span style={{ color: 'var(--text-secondary)' }}>Webhook per notifiche in tempo reale</span>
                      </li>
                      <li className="flex gap-3">
                        <CheckCircle2 className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                        <span style={{ color: 'var(--text-secondary)' }}>Documentazione completa e SDK</span>
                      </li>
                    </ul>
                    <div className="mt-4">
                      <span className="inline-flex items-center gap-2 text-sm font-semibold" style={{ color: 'var(--accent-primary)' }}>
                        Esplora la documentazione <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <Globe className="w-32 h-32" style={{ color: 'var(--accent-primary)', opacity: 0.2 }} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </a>
        </div>
      </section>

      {/* Developers Section */}
      <section className="section">
        <div className="text-center mb-12">
          <div className="inline-block px-4 py-2 rounded-full mb-4" style={{ background: 'var(--accent-wash)', color: 'var(--accent-text)' }}>
            <span className="text-sm font-semibold">👨‍💻 Per Sviluppatori</span>
          </div>
          <h2 className="heading-2 mb-4">Integra le nostre API nella tua applicazione</h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
            Accedi alle nostre potenti API per validazione indirizzi, geocoding e molto altro
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-12">
          <Card className="feature-card">
            <CardHeader>
              <div className="icon-container">
                <Code className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
              </div>
              <CardTitle>API RESTful</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
                API moderne e facili da integrare con documentazione completa ed esempi di codice in multipli linguaggi.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />
                  <span style={{ color: 'var(--text-secondary)' }}>Autenticazione sicura con API Keys</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />
                  <span style={{ color: 'var(--text-secondary)' }}>Rate limiting e monitoraggio</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />
                  <span style={{ color: 'var(--text-secondary)' }}>Supporto JSON per richieste e risposte</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="feature-card">
            <CardHeader>
              <div className="icon-container">
                <FileText className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
              </div>
              <CardTitle>Documentazione Completa</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
                Guide dettagliate, reference API e SDK per iniziare rapidamente lo sviluppo della tua integrazione.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />
                  <span style={{ color: 'var(--text-secondary)' }}>Esempi in Python, JavaScript, C#</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />
                  <span style={{ color: 'var(--text-secondary)' }}>Guide step-by-step</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />
                  <span style={{ color: 'var(--text-secondary)' }}>Supporto tecnico dedicato</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

        <div className="text-center">
          <div className="inline-flex flex-col sm:flex-row gap-4">
            <Button 
              onClick={() => window.location.href = '/developer-auth'} 
              className="btn-primary"
              size="lg"
            >
              <Code className="w-5 h-5 mr-2" />
              Registrati come Sviluppatore
            </Button>
            <Button 
              onClick={() => window.location.href = '/developers'} 
              variant="outline"
              size="lg"
            >
              <FileText className="w-5 h-5 mr-2" />
              Vai alla Documentazione
            </Button>
          </div>
          <p className="text-sm mt-4" style={{ color: 'var(--text-muted)' }}>
            Già registrato? <a href="/developer-auth" className="underline font-medium" style={{ color: 'var(--accent-primary)' }}>Accedi qui</a>
          </p>
        </div>
      </section>

      {/* How APIs Work Section */}
      <section className="section section-alt">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">Come funzionano le nostre API</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Un processo semplice in 3 step per integrare le nostre API
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-2xl font-bold" style={{ background: 'var(--accent-primary)', color: 'white' }}>
                1
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
                Registrati
              </h3>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Crea il tuo account sviluppatore gratuitamente in pochi secondi
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-2xl font-bold" style={{ background: 'var(--accent-primary)', color: 'white' }}>
                2
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
                Ottieni le credenziali
              </h3>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Ricevi le tue API keys per autenticare le richieste
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-2xl font-bold" style={{ background: 'var(--accent-primary)', color: 'white' }}>
                3
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
                Inizia a sviluppare
              </h3>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Segui la documentazione e integra le API nella tua app
              </p>
            </div>
          </div>

          <div className="mt-12 p-6 rounded-lg" style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)' }}>
            <h4 className="font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Esempio di utilizzo:</h4>
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
              <code>{`// Esempio chiamata API
const response = await fetch('https://spedireonline.net/api/v1/addresses/validate', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    address: {
      street: 'Via Roma 123',
      city: 'Milano',
      postalCode: '20100',
      country: 'IT'
    }
  })
});

const data = await response.json();
console.log(data);`}</code>
            </pre>
          </div>
        </div>
      </section>

      {/* Quick Access Links Section */}
      <section id="access" className="section">
        <div className="text-center mb-12">
          <h2 className="heading-2 mb-4">Accesso rapido</h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>Accedi direttamente alle nostre piattaforme operative</p>
        </div>
        
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <a 
              href="https://web.spedireonline.cloud/globaltrs/new-ship" 
              target="_blank" 
              rel="noopener noreferrer"
              className="feature-card block group"
            >
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 rounded-lg flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
                      <Package className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
                    </div>
                    <div className="flex-1">
                      <h3 className="heading-3 group-hover:text-[var(--accent-primary)] transition-colors">Spedisci Adesso</h3>
                      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Crea una nuova spedizione</p>
                    </div>
                    <ArrowRight className="w-6 h-6 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--accent-primary)' }} />
                  </div>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    Accedi direttamente al modulo di creazione spedizione per inviare i tuoi pacchi.
                  </p>
                </CardContent>
              </Card>
            </a>

            <a 
              href="https://web.spedireonline.cloud/globaltrs/Account/Login" 
              target="_blank" 
              rel="noopener noreferrer"
              className="feature-card block group"
            >
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 rounded-lg flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
                      <Users className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
                    </div>
                    <div className="flex-1">
                      <h3 className="heading-3 group-hover:text-[var(--accent-primary)] transition-colors">Accedi al Portale</h3>
                      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Area clienti</p>
                    </div>
                    <ArrowRight className="w-6 h-6 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--accent-primary)' }} />
                  </div>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    Accedi alla tua area personale per gestire spedizioni, rubrica e statistiche.
                  </p>
                </CardContent>
              </Card>
            </a>

            <a 
              href="https://www.bssi.it/shop/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="feature-card block group"
            >
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 rounded-lg flex items-center justify-center overflow-hidden" style={{ background: 'white' }}>
                      <img 
                        src="https://customer-assets.emergentagent.com/job_ship-smart-6/artifacts/2ykcjkxa_logo.png" 
                        alt="BSSI Logo"
                        className="w-12 h-12 object-contain"
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="heading-3 group-hover:text-[var(--accent-primary)] transition-colors">BSSI Shop</h3>
                      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>E-commerce materiali</p>
                    </div>
                    <ArrowRight className="w-6 h-6 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--accent-primary)' }} />
                  </div>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    Acquista materiali per imballaggio, etichette e accessori per le tue spedizioni.
                  </p>
                </CardContent>
              </Card>
            </a>

            <a 
              href="https://www.bssi.it" 
              target="_blank" 
              rel="noopener noreferrer"
              className="feature-card block group"
            >
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 rounded-lg flex items-center justify-center overflow-hidden" style={{ background: 'white' }}>
                      <img 
                        src="https://customer-assets.emergentagent.com/job_ship-smart-6/artifacts/2ykcjkxa_logo.png" 
                        alt="BSSI Logo"
                        className="w-12 h-12 object-contain"
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="heading-3 group-hover:text-[var(--accent-primary)] transition-colors">BSSI</h3>
                      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Sito aziendale</p>
                    </div>
                    <ArrowRight className="w-6 h-6 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--accent-primary)' }} />
                  </div>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    Scopri di più sulla nostra azienda, servizi e soluzioni per la logistica.
                  </p>
                </CardContent>
              </Card>
            </a>
          </div>
        </div>
      </section>

      {/* Demo Request Section */}
      <section id="demo" className="section">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="heading-2 mb-4">Richiedi una demo gratuita</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>Scopri come spedireonline.net può trasformare il tuo business</p>
          </div>
          <Card>
            <CardContent className="pt-6">
              <form onSubmit={handleDemoSubmit} className="space-y-4">
                <div>
                  <Input 
                    placeholder="Nome e Cognome" 
                    value={demoForm.name}
                    onChange={(e) => setDemoForm({...demoForm, name: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Input 
                    type="email" 
                    placeholder="Email" 
                    value={demoForm.email}
                    onChange={(e) => setDemoForm({...demoForm, email: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Input 
                    placeholder="Azienda" 
                    value={demoForm.company}
                    onChange={(e) => setDemoForm({...demoForm, company: e.target.value})}
                  />
                </div>
                <div>
                  <Input 
                    type="tel" 
                    placeholder="Telefono" 
                    value={demoForm.phone}
                    onChange={(e) => setDemoForm({...demoForm, phone: e.target.value})}
                  />
                </div>
                <Button type="submit" className="w-full btn-primary" disabled={demoLoading}>
                  {demoLoading ? 'Invio in corso...' : 'Richiedi Demo'} {!demoLoading && <ArrowRight className="w-5 h-5 ml-2" />}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section section-alt">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="heading-2 mb-4">Contattaci</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>Hai domande? Il nostro team è qui per aiutarti</p>
          </div>
          <Card>
            <CardContent className="pt-6">
              <form onSubmit={handleContactSubmit} className="space-y-4">
                <div>
                  <Input 
                    placeholder="Nome e Cognome" 
                    value={contactForm.name}
                    onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Input 
                    type="email" 
                    placeholder="Email" 
                    value={contactForm.email}
                    onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Textarea 
                    placeholder="Il tuo messaggio" 
                    rows={5}
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    required
                  />
                </div>
                <Button type="submit" className="w-full btn-primary" disabled={contactLoading}>
                  {contactLoading ? 'Invio in corso...' : 'Invia Messaggio'} {!contactLoading && <ArrowRight className="w-5 h-5 ml-2" />}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="section">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="heading-2 mb-4">Resta aggiornato</h2>
          <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>Iscriviti alla newsletter per ricevere novità, tips e aggiornamenti</p>
          <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3">
            <Input 
              type="email" 
              placeholder="La tua email" 
              className="flex-1"
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              required
            />
            <Button type="submit" className="btn-primary" disabled={newsletterLoading}>
              {newsletterLoading ? 'Invio...' : 'Iscriviti'}
            </Button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t" style={{ borderColor: 'var(--border-light)', background: 'var(--bg-section)' }}>
        <div className="section">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_ship-smart-6/artifacts/4h2rj0xf_logoonly.png" 
                  alt="spedireonline.net" 
                  className="h-8 w-8 object-contain"
                />
                <span className="font-bold" style={{ color: 'var(--brand-primary)' }}>spedireonline.net</span>
              </div>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>La piattaforma intelligente per gestire tutte le tue spedizioni.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Prodotto</h4>
              <ul className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                <li><button onClick={() => scrollToSection('features')}>Funzionalità</button></li>
                <li><button onClick={() => scrollToSection('dashboard')}>Dashboard</button></li>
                <li><button onClick={() => scrollToSection('benefits')}>Vantaggi</button></li>
                <li><a href="/developers">API per Sviluppatori</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Azienda</h4>
              <ul className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                <li><a href="#">Chi siamo</a></li>
                <li><a href="#">Carriere</a></li>
                <li><a href="#">Blog</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Supporto</h4>
              <ul className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                <li><button onClick={() => scrollToSection('contact')}>Contatti</button></li>
                <li><a href="#">FAQ</a></li>
                <li>
                  <a 
                    href="https://www.iubenda.com/privacy-policy/99691573" 
                    className="iubenda-white iubenda-noiframe iubenda-embed" 
                    title="Privacy Policy"
                  >
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a 
                    href="https://www.iubenda.com/privacy-policy/99691573/cookie-policy" 
                    className="iubenda-white iubenda-noiframe iubenda-embed" 
                    title="Cookie Policy"
                  >
                    Cookie Policy
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t text-center text-sm" style={{ borderColor: 'var(--border-light)', color: 'var(--text-muted)' }}>
            <p>© 2024 spedireonline.net. Tutti i diritti riservati.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;